import { AbstractMesh } from '@babylonjs/core';
import { SceneLoader } from '@babylonjs/core/Loading/sceneLoader';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { Scene } from '@babylonjs/core/scene';
import '@babylonjs/loaders/glTF';

import { getModelByNameApiCall } from '../../api/core-service/configurator/getModel';
import { getArticleFullDefinitionByIdApiCall } from '../../api/core-service/wsc/articles/getArticleFullDefinitionByIdApiCall';
import { Article } from '../../interfaces/Article';
import { setMaterialsTags } from '../../utils/3d/Material/setMaterialsTags';
import { applyPreRendeingOptions } from '../../utils/3d/Mesh/applyPreRendeingOptions';
import { getModelCustomUrl, getModelUrl } from '../../utils/assetsUrlBuilder';

const ROOT = '__root__';

export default class AssetsController {
  private readonly scene: Scene;

  constructor(scene: Scene) {
    this.scene = scene;
  }

  async downloadAndImportArticleToScene(sourceArticle: Article) {
    let model3d;
    if (sourceArticle.customModelName) {
      try {
        model3d = await getModelByNameApiCall(sourceArticle.customModelName);
      } catch (ex) {
        let articleDefinition = await getArticleFullDefinitionByIdApiCall(
          sourceArticle.definition.articleId,
        );
        if (articleDefinition.models) {
          model3d = articleDefinition.models[0];
        } else {
          model3d = sourceArticle.definition.model3d;
        }
      }
    } else {
      model3d = sourceArticle.definition.model3d;
    }
    const articleInstanceId = sourceArticle.id;
    let assetUrl = model3d ? getModelCustomUrl(model3d.url) : getModelUrl(sourceArticle.number);
    const container = await SceneLoader.LoadAssetContainerAsync(assetUrl, null, this.scene);
    // Apparently animations can interfere in model importing resulting in not correctly imported mesh
    // This is probably a bug in Babylon.js or in the model itself
    this.scene.stopAllAnimations();

    container.meshes.forEach((mesh) => {
      mesh.animations = [];
    });
    container.animationGroups.forEach((animationGroup) => {
      animationGroup.dispose();
    });
    container.animationGroups = [];
    container.animations = [];
    const root = container.meshes.find((m) => m.id === ROOT);
    if (!root) {
      throw new Error('Root node is not found in GLB file');
    }
    if (model3d) {
      /**
       * HACK: the wsc legacy scene requires to rotate additionally in 90 degrees.
       * When the admin scene has same glb settings as sandbox.babylon.js, standard for all GLBs
       */
      const adjustmentForCamera = 30;
      const currentRotation = model3d.rotation || [0, 0, 0];
      const rotation = [...currentRotation];
      rotation[1] = currentRotation[1] + 90 + adjustmentForCamera;
      const options = { ...model3d, ...{ rotation } };
      applyPreRendeingOptions(root, options);
    }
    setMaterialsTags(root, articleInstanceId, model3d);
    container.addAllToScene();
    return container;
  }

  loadArticleMesh = async (sourceArticle: Article): Promise<AbstractMesh> => {
    let result;
    try {
      result = await this.downloadAndImportArticleToScene(sourceArticle);
    } catch (ex) {
      this.scene.dispose();
      throw new Error('GLB_FILE_COULD_NOT_BE_LOADED');
    }
    if (!result || result.meshes.length === 0) {
      throw new Error(`Mesh couldn't be added to the scene`);
    }

    const meshes = result.meshes;
    const mainNode = meshes[0];
    mainNode.name = sourceArticle.id;

    this.improveMeshAndMaterialDisplay(meshes);
    return mainNode;
  };

  // Adjust material for colors to be displayed better in 3D scene
  private improveMeshAndMaterialDisplay(meshes) {
    meshes.forEach((mesh) => {
      // Adjust tabletop to be not reflective to lights
      if (mesh.name.includes('tabletop') && mesh.material.getClassName() === 'PBRMaterial') {
        const converted = mesh.material as PBRMaterial;
        converted.specularIntensity = 0;
      }

      // Adjust bright metal to not be as reflective to the point where it looses it's color
      if (mesh.material && mesh.material.name === 'BrightMetal') {
        const converted = mesh.material as PBRMaterial;
        converted.metallic = 0.5;
        converted.roughness = 0.2;
      }
    });
  }

  loadAllArticles = async (
    articles: Article[],
  ): Promise<{ article: Article; mesh: AbstractMesh }[]> =>
    Promise.all(
      articles.map(async (article) => {
        const mesh = await this.loadArticleMesh(article);
        return { article, mesh };
      }),
    );
}
